import React, { useState, useEffect } from 'react';
import { Printer, Trash2 } from 'lucide-react';
import OrderCommissionForm from './OrderCommissionForm';
import OrderCommissionView from './OrderCommissionView';
import DataTable from '../common/DataTable';
import { adminService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { toSafeFixed } from '../../utils/numberUtils';

interface OrderCommission {
  id: number;
  commission_number: string;
  order_type: string;
  commission_type: string;
  commission_value: number;
  order_id: number;
  order_number: string;
  invoice_id: number;
  invoice_number: string;
  agency_id: number;
  agency_name: string;
  agency_phone: string;
  invoice_final_bill_amount: number;
  total_amount: number;
  disc_percentage: number;
  disc_amount: number;
  roundoff: number;
  final_amount: number;
  voucher_id: number;
  paid_amount: number;
  balance_amount: number;
  payment_status: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string | null;
}

const OrderCommissionManagement: React.FC = () => {
  const [commissions, setCommissions] = useState<OrderCommission[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);
  const [resetForm, setResetForm] = useState(false);
  const [viewingCommission, setViewingCommission] = useState<OrderCommission | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<'list' | 'view'>('list');
  const { showToast } = useToast();

  useEffect(() => {
    loadCommissions();
  }, []);

  const loadCommissions = async () => {
    setLoading(true);
    try {
      const response = await adminService.getOrderCommissions();
      setCommissions(response.data.data || []);
    } catch (error) {
      showToast('error', 'Failed to load order commissions');
      setCommissions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    loadCommissions();
    setResetForm(true);
    setTimeout(() => setResetForm(false), 100);
  };

  const handleView = (commission: OrderCommission) => {
    setViewingCommission(commission);
    setActiveTab('view');
  };

  const handleDelete = async (commission: OrderCommission) => {
    if (!window.confirm(`Delete order commission ${commission.order_number}?`)) return;
    
    try {
      await adminService.deleteOrderCommission(commission.id);
      showToast('success', 'Order commission deleted successfully');
      loadCommissions();
    } catch (error) {
      showToast('error', 'Failed to delete order commission');
    }
  };

  const columns = [
    {
      key: 'commission_number',
      label: 'Commission #',
      sortable: true,
    },
    {
      key: 'order_number',
      label: 'Order Number',
      sortable: true,
    },
    {
      key: 'order_type',
      label: 'Type',
      sortable: true,
    },
    {
      key: 'commission_type',
      label: 'Commission Type',
      sortable: true,
    },
    {
      key: 'agency_name',
      label: 'Agency',
      sortable: true,
      render: (value: string) => value || 'N/A',
    },
    {
      key: 'invoice_final_bill_amount',
      label: 'Invoice Amount',
      sortable: true,
      render: (value: number) => toSafeFixed(value || 0, 2),
    },
    {
      key: 'total_amount',
      label: 'Commission Amount',
      sortable: true,
      render: (value: number) => toSafeFixed(value || 0, 2),
    },
    {
      key: 'paid_amount',
      label: 'Paid Amount',
      sortable: true,
      render: (value: number) => toSafeFixed(value || 0, 2),
    },
    {
      key: 'balance_amount',
      label: 'Balance',
      sortable: true,
      render: (value: number) => toSafeFixed(value || 0, 2),
    },
    {
      key: 'payment_status',
      label: 'Payment Status',
      sortable: true,
      render: (value: string) => (
        <span className={`px-2 py-1 text-xs font-medium rounded ${
          value === 'PAID' ? 'bg-green-100 text-green-800' :
          value === 'PARTIAL' ? 'bg-yellow-100 text-yellow-800' :
          'bg-red-100 text-red-800'
        }`}>
          {value}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' }),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: any, commission: OrderCommission) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleView(commission)}
            className="text-blue-600 hover:text-blue-800"
            title="Print Commission"
          >
            <Printer className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDelete(commission)}
            className="text-red-600 hover:text-red-800"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  if (activeTab === 'view' && viewingCommission) {
    return (
      <div style={{ padding: 'var(--erp-spacing-lg)' }}>
        <OrderCommissionView
          commissionId={viewingCommission.id}
          onBack={() => setActiveTab('list')}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 'var(--erp-spacing-lg)' }}>
      <OrderCommissionForm
        onSave={handleSave}
        isCollapsed={isFormCollapsed}
        onToggleCollapse={() => setIsFormCollapsed(!isFormCollapsed)}
        resetForm={resetForm}
      />

      <DataTable
        title="Order Commission Management"
        data={commissions}
        columns={columns}
        loading={loading}
      />
    </div>
  );
};

export default OrderCommissionManagement;
